import { IExpressionEvaluator } from "../../../Models/IExpressionEvaluator";
import { MaybePromise } from "../../../Models/MaybePromise";
import { IExpressionEvaluatorContext } from "../../../Models/IExpressionEvaluatorContext";
import { ViewModelLoadAssociationExpression } from "../../../Models/ViewModels/ViewModelLoadAssociationExpression";
import { EmptyGuid } from "./EmptyGuid";

export class ViewModelLoadAssociationExpressionEvaluator implements IExpressionEvaluator<ViewModelLoadAssociationExpression> {
  get type(): string {
    return "ViewModel.LoadAssociation";
  }

  evaluate(expression: ViewModelLoadAssociationExpression, context: IExpressionEvaluatorContext): MaybePromise {
    // Check if operating on a collection or single item
    if (!expression.TargetCollection) {
      return this.processSingleItem(expression, context);
    }

    const targetCollection = context.Evaluator.evaluate(expression.TargetCollection, context);

    if (targetCollection instanceof Promise) {
      return targetCollection.then((resolvedCollection) => this.processCollection(resolvedCollection, expression, context));
    }

    return this.processCollection(targetCollection, expression, context);
  }

  private async processSingleItem(expression: ViewModelLoadAssociationExpression, context: IExpressionEvaluatorContext): Promise<any> {
    let fetchedItem: any = null;

    // Step 1: Evaluate TargetItemId to get the ID (no $item context needed)
    const targetId = await context.Evaluator.evaluate(expression.TargetItemId, context);
    if (targetId !== undefined && targetId !== null && targetId !== EmptyGuid) {
      // Step 2: Fetch the item using FetchItem expression with $event.Id
      const fetchContext = context.CreateChild();
      fetchContext.VariableScope.set("$event", { Id: targetId });

      if (!expression.FetchItem) {
        throw new Error("FetchItem expression is not defined");
      }

      fetchedItem = await context.Evaluator.evaluate(expression.FetchItem, fetchContext);
      if (fetchedItem === undefined || fetchedItem === null) {
        console.warn("[ViewModelLoadAssociationEvaluator] FetchItem did not return a valid item");
      }
    }

    // Step 3: Assign the fetched item using TargetItem expression (no $item context needed)
    context.Evaluator.assign(expression.TargetItem, context, fetchedItem);

    return true;
  }

  private async processCollection(targetCollection: any, expression: ViewModelLoadAssociationExpression, context: IExpressionEvaluatorContext): Promise<any> {
    if (!Array.isArray(targetCollection) || targetCollection.length === 0) {
      // Nothing to process
      return targetCollection;
    }

    // Step 2: Collect all IDs from the collection using TargetItemId expression
    const targetItemContext = context.CreateChild();
    const targetIds: any[] = [];
    for (const item of targetCollection) {
      // Create child context with $item variable
      targetItemContext.VariableScope.set("$item", item);

      // Evaluate TargetItemId to get the ID
      const itemId = await context.Evaluator.evaluate(expression.TargetItemId, targetItemContext);
      if (itemId !== undefined && itemId !== null && itemId !== EmptyGuid) {
        targetIds.push(itemId);
      }
    }

    // Step 3: Fetch items using FetchItems expression with $event.Ids
    const fetchContext = context.CreateChild();
    fetchContext.VariableScope.set("$event", { Ids: targetIds });

    if (!expression.FetchItems) {
      throw new Error("FetchItems expression is not defined");
    }

    const fetchedItems = await context.Evaluator.evaluate(expression.FetchItems, fetchContext);

    if (!Array.isArray(fetchedItems)) {
      console.warn("[ViewModelLoadAssociationEvaluator] FetchItems did not return an array, got result:", fetchedItems);
      return targetCollection;
    }

    // Step 4: Create a map of fetched items by their ID for fast lookup
    const fetchedItemContext = context.CreateChild();
    const fetchedItemsMap = new Map<any, any>();
    for (const fetchedItem of fetchedItems) {
      // Create child context with $item variable to evaluate ID from fetched item
      fetchedItemContext.VariableScope.set("$item", fetchedItem);
      const fetchedItemId = await context.Evaluator.evaluate(expression.SourceItemId, fetchedItemContext);

      fetchedItemsMap.set(fetchedItemId, fetchedItem);
    }

    // Create a copy of the targetCollection and return it instead of modifying in place
    const newTargetCollection = [...targetCollection];

    // Step 5: Match and assign fetched items to the original collection items
    for (const targetItem of newTargetCollection) {
      // Create child context with $item variable
      targetItemContext.VariableScope.set("$item", targetItem);

      // Get the item ID
      const itemId = await context.Evaluator.evaluate(expression.TargetItemId, targetItemContext);

      // Find the matching fetched item
      const matchedItem = fetchedItemsMap.get(itemId);
      const itemValue = matchedItem !== undefined ? matchedItem : null;

      // Assign the matched item using TargetItem expression
      context.Evaluator.assign(expression.TargetItem, targetItemContext, itemValue);
    }

    if (expression.TargetCollection) {
      // Finally, assign the updated collection back
      context.Evaluator.assign(expression.TargetCollection, context, newTargetCollection);
    }

    return true;
  }
}
